import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import ReportActions from 'components/reports/ReportActions';
import { Separator } from 'components/ui/separator';
import {
  useDeleteSymptomReports,
  useGetSymptomReportDetail,
} from 'services/symptom-checker/hooks';
import ReportDetailInfo from './ReportDetailInfo';
import Loader from 'components/Loader';
import NoData from 'components/NoData';
import { downloadFile } from 'lib/utils';
import { ScrollArea } from 'components/ui/scroll-area';
import useAuthStore from 'store/auth';
import SymptomCheckerGenerating from './generate/Generating';

export default function ReportDetail({ tokenId }: { tokenId: string }) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const {
    data: reportDetail,
    isFetching,
    isError,
  } = useGetSymptomReportDetail({
    tokenId: tokenId,
  });

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const { mutate: deleteReport, isPending: isDeleting } =
    useDeleteSymptomReports({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['inifinite-symptom-reports', profileId],
        });
        //  setShowDelete(false);
        //  navigation.goBack();
      },
      onError: (error) => {
        //  errorToast(error.message);
        toast.error(error.message);
      },
    });

  const onDownload = async (url: string | undefined) => {
    setIsDownloading(true);
    try {
      await downloadFile(url);
    } catch (e) {
      console.error(e);
    } finally {
      setIsDownloading(false);
    }
  };

  const onDelete = () => {
    deleteReport([tokenId]);
  };

  if (isFetching) {
    return <Loader />;
  }

  if (isError || !reportDetail?.length) {
    return (
      <div className='h-[calc(100vh-12rem)]'>
        <NoData className='border-none' message={t('no_report_data')} />
      </div>
    );
  }

  if (reportDetail[0].status === 'processed') {
    return <SymptomCheckerGenerating isDetails />;
  }

  const reportActions = () => {
    return (
      <ReportActions
        content={reportDetail?.[0]?.report_link || ''}
        isDownloading={isDownloading}
        isDeleting={isDeleting}
        onDelete={onDelete}
        onDownload={() => onDownload(reportDetail?.[0]?.report_link)}
        disabled={!reportDetail?.length}
      />
    );
  };

  return (
    <div>
      <div className='mb-2'>{reportActions()}</div>

      <Separator className='w-full' />

      <ScrollArea className='h-[calc(100vh-12rem)] mt-4'>
        <ReportDetailInfo detail={reportDetail?.[0]} />
      </ScrollArea>
    </div>
  );
}
